import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { fetchScanJob, resumeScanJob } from "../../api/scans";

const ScanJobProgress = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [polling, setPolling] = useState(true);

  useEffect(() => {
    let intervalId;

    const loadJob = async () => {
      try {
        const data = await fetchScanJob(jobId);
        setJob(data);

        // Stop polling if job is finished
        if (data.status === "completed" || data.status === "failed") {
          setPolling(false);
        }

        setLoading(false);
      } catch (err) {
        setError("Failed to load scan job");
        console.error(err);
        setLoading(false);
      }
    };

    loadJob();

    if (polling) {
      intervalId = setInterval(loadJob, 2000); // Poll every 2 seconds
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [jobId, polling]);

  const handleResume = async () => {
    try {
      const data = await resumeScanJob(jobId);
      setJob(data);
      setPolling(true);
    } catch (err) {
      setError("Failed to resume scan job");
      console.error(err);
    }
  };

  if (loading) {
    return (
      <div className="scan-job-progress">
        <div className="loading-state">Loading scan job...</div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="scan-job-progress">
        <div className="error-state">Scan job not found</div>
      </div>
    );
  }

  const isCompleted = job.status === "completed";
  const isFailed = job.status === "failed";
  const canResume = (job.status === "failed" || job.status === "paused") && job.error;

  return (
    <div className="scan-job-progress">
      <div className="job-header">
        <h2>Scan Job #{job.id}</h2>
        <div className="job-meta">
          <span className={`status-badge ${job.status}`}>{job.status.toUpperCase()}</span>
          <span className="target-info">Target: {job.target}</span>
        </div>
      </div>

      {error && <div className="error-banner">{error}</div>}

      <div className="job-content">
        {/* Progress Bar */}
        <div className="progress-section">
          <h3>Overall Progress</h3>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${Math.min(job.progress || 0, 100)}%` }}
            />
          </div>
          <p className="progress-text">{job.progress || 0}% complete</p>
        </div>

        {/* Current Tool */}
        {job.current_tool && !isCompleted && !isFailed && (
          <div className="current-tool-section">
            <h3>Current Tool</h3>
            <p className="current-tool-name">{job.current_tool}</p>
          </div>
        )}

        {/* Tool Progress */}
        {job.tool_progress && job.tool_progress.length > 0 && (
          <div className="tool-progress-section">
            <h3>Tool Progress</h3>
            <div className="tool-progress-list">
              {job.tool_progress.map(tool => (
                <div key={tool.id} className={`tool-progress-item ${tool.status}`}>
                  <div className="tool-status-icon">
                    {tool.status === "completed" && "✓"}
                    {tool.status === "running" && "⏳"}
                    {tool.status === "pending" && "○"}
                  </div>
                  <div className="tool-info">
                    <span className="tool-name">{tool.name}</span>
                    <span className="tool-progress">{tool.progress}%</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Console Output */}
        {job.console_lines && job.console_lines.length > 0 && (
          <div className="console-section">
            <h3>Console Output</h3>
            <div className="console-box">
              {job.console_lines.map((line, idx) => (
                <div key={idx} className="console-line">
                  {line}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Error Display */}
        {isFailed && job.error && (
          <div className="error-section">
            <h3>Error Details</h3>
            <p className="error-message">{job.error}</p>
            {canResume && (
              <button type="button" className="resume-button" onClick={handleResume}>
                Resume Scan
              </button>
            )}
          </div>
        )}

        {/* Completed State */}
        {isCompleted && (
          <div className="completed-section">
            <h3>✓ Scan Completed</h3>
            <div className="results-summary">
              <div className="result-item">
                <span className="result-label">Risk Score:</span>
                <span className="result-value">{job.risk_score || 0}</span>
              </div>
              <div className="result-item">
                <span className="result-label">Status:</span>
                <span className="result-value">{job.status}</span>
              </div>
              {job.finished_at && (
                <div className="result-item">
                  <span className="result-label">Completed:</span>
                  <span className="result-value">{new Date(job.finished_at).toLocaleString()}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="job-actions">
        <button type="button" className="secondary-button" onClick={() => navigate("/")}>
          ← Back to Dashboard
        </button>
      </div>
    </div>
  );
};

export default ScanJobProgress;
